import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { Button as ButtonShad } from 'src/components/ui/button';
import { useEffect, useState } from 'react';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { EditIcon } from 'src/assets/icons/editIcon';
import { baseApi, useGetAllProjectsQuery, useUpdateProjectMutation } from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { useAppDispatch } from 'src/store';
import type { Project } from 'src/services/projectsService';

export const ChangeAiModelModal = ({
  disabled,
  currentProject,
}: {
  disabled: boolean;
  currentProject: Pick<
    Project,
    'id' | 'name' | 'cocbi_project_id' | 'ai_reply_generation' | 'ai_text_lense'
  >;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const notification = useNotification();
  const dispatch = useAppDispatch();

  const [updateProject, { isLoading, isSuccess }] = useUpdateProjectMutation();

  const { data: projects = [] } = useGetAllProjectsQuery();

  useEffect(() => {
    if (isSuccess) {
      setIsDialogOpen(false);
      notification.success('AI-model was changed!');
    }
  }, [isLoading, isSuccess]);

  const projectsByType = projects?.reduce(
    (acc, project) => {
      if (!acc[project?.project_type]) {
        acc[project.project_type] = [];
      }
      acc[project?.project_type].push(project);
      return acc;
    },
    { agent: [], bi: [], customer: [] },
  );

  // Radio values are strings; '0' is the default model.
  const currentModelValue = String(Number(currentProject?.cocbi_project_id) || 0);

  const { setValue, handleSubmit, control, watch } = useForm({
    defaultValues: {
      projectId: currentModelValue,
    },
    resolver: formResolver(
      object({
        projectId: stringScheme({ required: false }).label('Project'),
      }),
    ),
  });
  const selectedAccountId = watch('projectId');

  const dialogOpenHandler = (open: boolean) => {
    setIsDialogOpen(open);
    if (open) {
      setValue('projectId', currentModelValue);
    }
  };

  const submit = (data: { projectId: string }) => {
    updateProject({
      name: currentProject.name,
      projectId: currentProject.id,
      ai_reply_generation: currentProject.ai_reply_generation,
      ai_text_lense: currentProject.ai_text_lense,
      cocbi_project_id: Number(data.projectId),
      use_cocbi_model: Boolean(Number(data.projectId)),
    }).then(() => dispatch(baseApi.util.invalidateTags(['ProjectById'])));
  };

  const disableCondition = Number(currentProject?.cocbi_project_id) === Number(selectedAccountId);

  return (
    <Dialog onOpenChange={dialogOpenHandler} open={isDialogOpen}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <ButtonShad
          variant="ghost"
          size="icon"
          className={clsx(disabled && 'pointer-events-none opacity-50')}
        >
          <EditIcon />
        </ButtonShad>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change AI-model</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <p className="opacity-50 font-medium text-xs"> All available AI-models</p>
          <form
            role="none"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(submit)();
              }
            }}
            onSubmit={handleSubmit(submit)}
            id="model-form"
            className="bg-white p-5 rounded-sm-5 border border-[#B8C1C666]"
          >
            <Controller
              control={control}
              name="projectId"
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onChange={onChange}
                  onValueChange={onChange}
                  defaultValue={value}
                  className="flex flex-col space-y-1"
                >
                  <label
                    htmlFor="reset"
                    className="flex items-center space-x-5 space-y-0 border rounded-sm-5 px-5 py-4 cursor-pointer hover:border-black transition-colors"
                  >
                    <RadioGroupItem value={'0'} id="reset" />
                    <label htmlFor="reset" className="cursor-pointer">
                      <p className="font-medium text-base text-ebony opacity-70 hover:opacity-100 transition">
                        Default
                      </p>
                    </label>
                  </label>
                  {projectsByType.customer.map((project) => (
                    <label
                      htmlFor={project.name}
                      key={project.id}
                      className="flex items-center space-x-5 space-y-0 border rounded-sm-5 px-5 py-4 cursor-pointer hover:border-black transition-colors"
                    >
                      <RadioGroupItem value={`${project.id}`} id={project.name} />
                      <label htmlFor={project.name} className="cursor-pointer">
                        <p className="font-medium text-base text-ebony opacity-70 hover:opacity-100 transition">
                          {project.name}
                        </p>
                      </label>
                    </label>
                  ))}
                </RadioGroup>
              )}
            />
          </form>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <ButtonShad variant="pill-outline-dark" size="pill" style={{ width: 'auto' }}>
              Cancel
            </ButtonShad>
          </DialogClose>
          <ButtonShad
            variant="pill-blue"
            size="pill"
            disabled={isLoading || disableCondition}
            type="submit"
            form="model-form"
            style={{ width: 'auto' }}
          >
            {isLoading ? 'Loading...' : 'Change'}
          </ButtonShad>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
